import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from './contexts/AuthContext';
import { ChatContext } from './contexts/ChatContext';
import ChartRenderer from './ChartRenderer';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import ExportJsonExcel from 'js-export-excel';
import { PlusCircle, ClockHistory, Trash, Stars } from 'react-bootstrap-icons';
import RespuestaMarkdown from './ia/RespuestaMarkdown';

const styles = {
  container: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif'
  },
  button: {
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  chatWindow: {
    width: '350px',
    height: '500px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginBottom: '15px'
  },
  header: {
    backgroundColor: '#0d6efd',
    color: 'white',
    padding: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  messagesArea: {
    flex: 1,
    padding: '10px',
    overflowY: 'auto',
    backgroundColor: '#f9f9f9'
  },
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eee',
    display: 'flex'
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginRight: '5px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    resize: 'none',
    minHeight: '38px',
    maxHeight: '120px',
    overflowY: 'auto'
  },
  msgUser: {
    backgroundColor: '#e7f1ff',
    padding: '8px',
    borderRadius: '10px 10px 0 10px',
    marginBottom: '8px',
    alignSelf: 'flex-end',
    maxWidth: '80%',
    marginLeft: 'auto'
  },
  msgBot: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    padding: '8px',
    borderRadius: '10px 10px 10px 0',
    marginBottom: '8px',
    maxWidth: '95%'
  },
  headerBtn: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px'
  },
  convItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
    fontSize: '13px'
  }
};

const cleanDataForExport = (data) => {
  if (!Array.isArray(data)) return data;
  return data.map(row => {
    const cleanRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (key === '_id') continue;
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const keys = Object.keys(value);
        if (keys.includes('_bsontype') || keys.includes('$oid')) continue;
      }
      cleanRow[key] = value;
    }
    return cleanRow;
  });
};

const downloadExcel = (data, filename = 'datos') => {
  try {
    const cleaned = cleanDataForExport(Array.isArray(data) ? data : [data]);
    if (!cleaned || cleaned.length === 0) {
      alert('No hay datos disponibles para exportar');
      return;
    }
    const headers = Object.keys(cleaned[0]);
    const option = {
      fileName: filename,
      datas: [
        {
          sheetData: cleaned,
          sheetName: filename,
          sheetFilter: headers,
          sheetHeader: headers,
        },
      ],
    };
    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  } catch (error) {
    console.error('Error generando Excel:', error);
    alert('Error al generar el archivo Excel');
  }
};

const downloadPDF = (messageContent, rows, filename = 'reporte') => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let y = 20;

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(`Reporte ${process.env.REACT_APP_NOMBRE_INSTITUCION || 'Asistente IA'}`, 14, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Fecha: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    if (messageContent) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Respuesta:', 14, y);
      y += 8;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      const splitText = doc.splitTextToSize(messageContent, pageWidth - 28);
      doc.text(splitText, 14, y);
      y += (splitText.length * 5) + 10;
    }

    const cleaned = cleanDataForExport((rows || []).slice(0, 50));
    if (cleaned && cleaned.length > 0) {
      const headers = Object.keys(cleaned[0]);
      const body = cleaned.map(row => headers.map(h => row[h] ?? ''));
      doc.autoTable({
        head: [headers],
        body,
        startY: y,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8 },
        headStyles: { fillColor: [13, 110, 253] }
      });
    }

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`${filename}_${timestamp}.pdf`);
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el archivo PDF');
  }
};

const ChatWidget = () => {
  const { user, isAuth } = useContext(AuthContext);
  const {
    isOpen,
    setIsOpen,
    conversaciones,
    activeConv,
    messages,
    isLoading,
    loadingConv,
    cargarConversaciones,
    seleccionarConversacion,
    nuevaConversacion,
    eliminarConversacion,
    enviar
  } = useContext(ChatContext);

  const [input, setInput] = useState('');
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && view === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen, view]);

  const abrirHistorial = () => {
    setView('history');
    cargarConversaciones();
  };

  useEffect(() => {
    if (!isLoading && isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, isOpen]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const currentInput = input;
    setInput('');
    await enviar(currentInput);
  };

  // AuthContext tarda un instante en re-hidratarse tras un full page reload (así navega el
  // menú de este proyecto, con href en vez de <Link>) — ocultar en vez de redirigir.
  if (!isAuth || !user || !user.menu_ia) {
    return null;
  }

  const rowsDeMensaje = (msg) => {
    if (msg.chart?.data?.length) return msg.chart.data;
    if (Array.isArray(msg.rawData)) {
      return msg.rawData.flatMap(r => r.rows || []);
    }
    return [];
  };

  return (
    <div style={styles.container}>
      {isOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.header}>
            <strong>{view === 'history' ? 'Historial' : `Asistente ${process.env.REACT_APP_NOMBRE_INSTITUCION || ''}`}</strong>
            <div style={{ display: 'flex', gap: '4px' }}>
              {view === 'chat' ? (
                <>
                  <button onClick={() => { nuevaConversacion(); setView('chat'); }} title="Nueva conversación" style={styles.headerBtn}>
                    <PlusCircle size={16} />
                  </button>
                  <button onClick={abrirHistorial} title="Historial" style={styles.headerBtn}>
                    <ClockHistory size={16} />
                  </button>
                  {activeConv && (
                    <button onClick={() => eliminarConversacion(activeConv._id)} title="Eliminar conversación" style={styles.headerBtn}>
                      <Trash size={16} />
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => setView('chat')} title="Volver al chat" style={styles.headerBtn}>
                  ← Volver
                </button>
              )}
              <button onClick={() => setIsOpen(false)} style={styles.headerBtn}>✕</button>
            </div>
          </div>

          {view === 'history' ? (
            <div style={{ ...styles.messagesArea, padding: 0 }}>
              {conversaciones.length === 0 && (
                <p style={{ textAlign: 'center', color: '#888', fontSize: '13px', padding: '20px' }}>Sin conversaciones aún.</p>
              )}
              {conversaciones.map((c) => (
                <div key={c._id} style={styles.convItem} onClick={async () => { await seleccionarConversacion(c._id); setView('chat'); }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.titulo}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); eliminarConversacion(c._id); }}
                    title="Eliminar"
                    style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer', marginLeft: '8px' }}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.messagesArea}>
              {loadingConv && <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>Cargando conversación…</p>}
              {!loadingConv && messages.length === 0 && (
                <div style={styles.msgBot}>¡Hola! Soy el asistente de Sistemify. Pregúntame sobre saldos de clientes, cargos, facturación o reparto de utilidades entre socios. También puedo generar gráficas.</div>
              )}
              {messages.map((msg, index) => {
                const rows = rowsDeMensaje(msg);
                return (
                  <div key={index}>
                    <div style={msg.role === 'user' ? styles.msgUser : styles.msgBot}>
                      {msg.role === 'user' ? <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div> : <RespuestaMarkdown texto={msg.text} />}
                      {msg.chart && <ChartRenderer config={msg.chart} />}
                    </div>

                    {msg.role === 'assistant' && rows.length > 0 && (
                      <div style={{ marginTop: '5px', marginBottom: '10px', display: 'flex', gap: '8px', paddingLeft: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => downloadPDF(msg.text, rows, 'reporte')}
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                          title="Descargar como PDF"
                        >
                          📄 PDF
                        </button>
                        <button
                          onClick={() => downloadExcel(rows, 'datos')}
                          style={{ backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                          title="Descargar como Excel"
                        >
                          📊 Excel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {isLoading && <div style={{ ...styles.msgBot, fontStyle: 'italic', color: '#666' }}>Analizando datos...</div>}
              <div ref={messagesEndRef} />
            </div>
          )}

          {view === 'chat' && (
            <form style={styles.inputArea} onSubmit={handleSend}>
              <textarea
                ref={inputRef}
                style={styles.input}
                value={input}
                onChange={handleInputChange}
                placeholder="Escribe tu pregunta..."
                disabled={isLoading}
                autoFocus
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
              />
              <button type="submit" disabled={isLoading} style={{ cursor: 'pointer', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 15px' }}>
                {isLoading ? '...' : 'Enviar'}
              </button>
            </form>
          )}
        </div>
      )}

      {!isOpen && (
        <button style={styles.button} onClick={() => setIsOpen(true)}>
          <Stars size={22} />
        </button>
      )}
    </div>
  );
};

export default ChatWidget;
